// services/drawingStorage.js
// Хранение чертежей пользователя в Firestore.
// Структура: users/{uid}/drawings/{drawingId}
// При ~100 KB на чертёж хранилище Firestore достаточно без Storage.

import {
  collection,
  collectionGroup,
  addDoc,
  deleteDoc,
  getDocs,
  doc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

function drawingsCol(uid) {
  return collection(db, 'users', uid, 'drawings');
}

/** Явное сохранение пользователем в «Историю» (облако) */
export const DRAWING_ORIGIN_CLOUD = 'cloud';
/** Копия при сохранении на компьютер (только для админ-просмотра) */
export const DRAWING_ORIGIN_DEVICE = 'device';

export function isCloudDrawing(entry) {
  const origin = entry?.origin || DRAWING_ORIGIN_CLOUD;
  return origin === DRAWING_ORIGIN_CLOUD;
}

/**
 * Сохранить чертёж в облако.
 * @param {string} uid
 * @param {object} drawingData
 * @param {string} fileName
 * @param {string|null} thumbnail — base64 data URL превью (PNG/JPEG), опционально
 * Возвращает id нового документа.
 */
export async function saveDrawingToCloud(uid, drawingData, fileName, thumbnail = null) {
  const jsonString = JSON.stringify(drawingData);
  const byteSize = new Blob([jsonString]).size;

  const colRef = drawingsCol(uid);
  const docRef = await addDoc(colRef, {
    fileName,
    savedAt: serverTimestamp(),
    byteSize,
    inlineData: jsonString,
    origin: DRAWING_ORIGIN_CLOUD,
    fileFormat: 'ptCAD',
    ...(thumbnail ? { thumbnail } : {}),
  });
  return docRef.id;
}

/**
 * Копия .ptCAD при сохранении на устройство (для админ-панели, не в истории пользователя).
 */
export async function savePtCadDeviceCopy(uid, drawingData, fileName, thumbnail = null) {
  const jsonString = JSON.stringify(drawingData);
  const byteSize = new Blob([jsonString]).size;
  const docRef = await addDoc(drawingsCol(uid), {
    fileName,
    savedAt: serverTimestamp(),
    byteSize,
    inlineData: jsonString,
    origin: DRAWING_ORIGIN_DEVICE,
    fileFormat: 'ptCAD',
    ...(thumbnail ? { thumbnail } : {}),
  });
  return docRef.id;
}

/**
 * Копия PDF при сохранении на устройство (для админ-панели).
 */
export async function savePdfDeviceCopy(uid, fileName, pdfBlob, thumbnail = null) {
  const buffer = await pdfBlob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  const pdfBase64 = btoa(binary);
  const byteSize = pdfBlob.size;
  const docRef = await addDoc(drawingsCol(uid), {
    fileName,
    savedAt: serverTimestamp(),
    byteSize,
    origin: DRAWING_ORIGIN_DEVICE,
    fileFormat: 'pdf',
    pdfBase64,
    ...(thumbnail ? { thumbnail } : {}),
  });
  return docRef.id;
}

/**
 * Загрузить список чертежей (метаданные, без данных).
 */
export async function loadDrawingList(uid) {
  const q = query(drawingsCol(uid), orderBy('savedAt', 'desc'));
  const snap = await getDocs(q);
  return mapDrawingDocs(snap.docs);
}

function mapDrawingDocs(docs) {
  return docs.map((d) => ({
    id: d.id,
    fileName: d.data().fileName,
    savedAt: d.data().savedAt?.toDate?.() || null,
    byteSize: d.data().byteSize,
    inlineData: d.data().inlineData,
    thumbnail: d.data().thumbnail || null,
    origin: d.data().origin || DRAWING_ORIGIN_CLOUD,
    fileFormat: d.data().fileFormat || 'ptCAD',
    pdfBase64: d.data().pdfBase64 || null,
  }));
}

/** Список только для «Истории чертежей» пользователя (без копий с ПК). */
export async function loadCloudDrawingList(uid) {
  const items = await loadDrawingList(uid);
  return items.filter(isCloudDrawing);
}

/**
 * Загрузить данные конкретного чертежа.
 */
export async function loadDrawingData(entry) {
  if (!entry.inlineData) throw new Error('Нет данных чертежа');
  return JSON.parse(entry.inlineData);
}

/**
 * Удалить чертёж из облака.
 */
export async function deleteDrawingFromCloud(uid, entry) {
  await deleteDoc(doc(db, 'users', uid, 'drawings', entry.id));
}

/** Удалить все копии с ПК (origin: device). Облачная история не затрагивается. */
export async function deleteAllDeviceDrawingCopies(uid) {
  const items = await loadDrawingList(uid);
  const deviceItems = items.filter((e) => !isCloudDrawing(e));
  await Promise.all(
    deviceItems.map((entry) => deleteDoc(doc(db, 'users', uid, 'drawings', entry.id))),
  );
  return deviceItems.length;
}

/** Удалить все копии с ПК у всех пользователей. */
export async function deleteAllDeviceDrawingCopiesGlobally() {
  const snap = await getDocs(collectionGroup(db, 'drawings'));
  const refs = [];
  snap.forEach((d) => {
    if (d.data().origin === DRAWING_ORIGIN_DEVICE) refs.push(d.ref);
  });
  await Promise.all(refs.map((ref) => deleteDoc(ref)));
  return refs.length;
}
